const MOBILE_THUMBNAILS_LEFT_MEDIA_QUERY = '(max-width: 999px)';

class ProductGalleryMobileThumbnailsHeight {
  constructor(gallery) {
    this.gallery = gallery;
    this.thumbnailWrapper = gallery.querySelector('.product-gallery__thumbnail-list-wrapper--mobile-left');
    this.carousel = gallery.querySelector('media-carousel');
    this._abortController = new AbortController();
    this._mediaQuery = window.matchMedia(MOBILE_THUMBNAILS_LEFT_MEDIA_QUERY);
    this._scheduledUpdate = null;

    if (!this.thumbnailWrapper || !this.carousel) {
      return;
    }

    this._bindEvents();
    this.update();
  }

  destroy() {
    this._abortController.abort();
    this._resizeObserver?.disconnect();
    this.thumbnailWrapper.style.height = '';
  }

  _bindEvents() {
    const { signal } = this._abortController;

    this.gallery.addEventListener('carousel:settle', () => this.scheduleUpdate(), { signal });
    this.gallery.addEventListener('carousel:change', () => this.scheduleUpdate(), { signal });
    this._mediaQuery.addEventListener('change', () => this.update(), { signal });
    window.addEventListener('resize', () => this.scheduleUpdate(), { signal });

    this.gallery.querySelectorAll('.product-gallery__media img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => this.scheduleUpdate(), { once: true, signal });
      }
    });

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this._resizeObserver.observe(this.carousel);
    }
  }

  scheduleUpdate() {
    if (this._scheduledUpdate) {
      return;
    }

    this._scheduledUpdate = requestAnimationFrame(() => {
      this._scheduledUpdate = null;
      this.update();
    });
  }

  _getSelectedMedia() {
    return this.gallery.querySelector('.product-gallery__media.is-selected')
      || this.gallery.querySelector('.product-gallery__media:not([hidden])');
  }

  _getSelectedMediaHeight() {
    const selectedMedia = this._getSelectedMedia();

    if (!selectedMedia) {
      return 0;
    }

    const image = selectedMedia.querySelector('img');

    if (image) {
      return Math.round(image.getBoundingClientRect().height);
    }

    return Math.round(selectedMedia.getBoundingClientRect().height);
  }

  update() {
    if (!this.thumbnailWrapper) {
      return;
    }

    if (!this._mediaQuery.matches) {
      this.thumbnailWrapper.style.height = '';
      return;
    }

    const height = this._getSelectedMediaHeight();

    if (height > 0) {
      this.thumbnailWrapper.style.height = `${height}px`;
    }
  }
}

function initProductGalleryMobileThumbnailHeights() {
  document.querySelectorAll('product-gallery.product-gallery--mobile-thumbnails-left').forEach((gallery) => {
    gallery._mobileThumbnailsHeight?.destroy();
    gallery._mobileThumbnailsHeight = new ProductGalleryMobileThumbnailsHeight(gallery);
  });
}

document.addEventListener('DOMContentLoaded', initProductGalleryMobileThumbnailHeights);
document.addEventListener('shopify:section:load', initProductGalleryMobileThumbnailHeights);
document.addEventListener('product:rerender', () => {
  requestAnimationFrame(initProductGalleryMobileThumbnailHeights);
});

function initProductGalleryLoadingGifs() {
  document.querySelectorAll('product-gallery.product-gallery--custom-loading-gif .product-gallery__media[data-media-type="image"]').forEach((media) => {
    const image = media.querySelector('img');

    if (!image) {
      return;
    }

    const markLoaded = () => media.classList.add('is-loaded');

    if (image.complete) {
      markLoaded();
      return;
    }

    media.classList.remove('is-loaded');

    if (image.dataset.productGalleryLoadingGifBound === 'true') {
      return;
    }

    image.dataset.productGalleryLoadingGifBound = 'true';
    image.addEventListener('load', markLoaded, { once: true });
    image.addEventListener('error', markLoaded, { once: true });
  });
}

document.addEventListener('DOMContentLoaded', initProductGalleryLoadingGifs);
document.addEventListener('shopify:section:load', initProductGalleryLoadingGifs);
document.addEventListener('product:rerender', () => {
  requestAnimationFrame(initProductGalleryLoadingGifs);
});

const DESKTOP_GALLERY_VIEW_MORE_MEDIA_QUERY = window.themeVariables?.breakpoints?.md || '(min-width: 1000px)';

class ProductGalleryViewMore {
  constructor(gallery) {
    this.gallery = gallery;
    this.initialCount = parseInt(gallery.getAttribute('view-more-initial'), 10);
    this.button = gallery.querySelector('.product-gallery__view-more-button');
    this.textEl = gallery.querySelector('.product-gallery__view-more-text');
    this.expanded = false;
    this._abortController = new AbortController();
    this._mediaQuery = window.matchMedia(DESKTOP_GALLERY_VIEW_MORE_MEDIA_QUERY);

    if (!this.button || !this.initialCount) {
      return;
    }

    const { signal } = this._abortController;

    this.button.addEventListener('click', () => this.toggle(), { signal });
    this.gallery.querySelector('media-carousel')?.addEventListener('carousel:filter', () => this.reset(), { signal });
    this._mediaQuery.addEventListener('change', () => this.update(), { signal });
    this.update();
  }

  destroy() {
    this._abortController.abort();
    this.gallery.classList.remove('is-expanded');
  }

  getVisibleMedia() {
    return Array.from(this.gallery.querySelectorAll('.product-gallery__media:not([hidden])'));
  }

  getHiddenCount() {
    return Math.max(0, this.getVisibleMedia().length - this.initialCount);
  }

  isDesktop() {
    return this._mediaQuery.matches;
  }

  update() {
    if (!this.isDesktop()) {
      this.getVisibleMedia().forEach((media) => {
        media.classList.remove('product-gallery__media--view-more-hidden');
      });
      this.gallery.classList.remove('is-expanded');
      this.button.hidden = true;
      return;
    }

    const hiddenCount = this.getHiddenCount();

    this.getVisibleMedia().forEach((media, index) => {
      media.classList.toggle('product-gallery__media--view-more-hidden', !this.expanded && index >= this.initialCount);
    });

    this.button.hidden = hiddenCount === 0 && !this.expanded;
    this.updateButtonText(hiddenCount);
    this.gallery.classList.toggle('is-expanded', this.expanded);
    this.button.setAttribute('aria-expanded', String(this.expanded));
  }

  updateButtonText(hiddenCount) {
    if (!this.textEl) {
      return;
    }

    if (this.expanded) {
      this.textEl.textContent = this.button.dataset.showLess || 'Show less';
      return;
    }

    const template = this.button.dataset.viewMoreTemplate || 'View more images (__COUNT__)';
    this.textEl.textContent = template.replace('__COUNT__', hiddenCount);
  }

  toggle() {
    if (!this.isDesktop()) {
      return;
    }

    this.expanded = !this.expanded;
    this.update();

    if (!this.expanded) {
      this.gallery.querySelector('.product-gallery__media-list-wrapper')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  reset() {
    this.expanded = false;
    this.update();
  }
}

function initProductGalleryViewMore() {
  document.querySelectorAll('product-gallery.product-gallery--view-more').forEach((gallery) => {
    gallery._viewMore?.destroy();
    gallery._viewMore = new ProductGalleryViewMore(gallery);
  });
}

document.addEventListener('DOMContentLoaded', initProductGalleryViewMore);
document.addEventListener('shopify:section:load', initProductGalleryViewMore);
document.addEventListener('product:rerender', () => {
  requestAnimationFrame(initProductGalleryViewMore);
});

function updateGalleryCounter(carousel, counter) {
  const currentEl = counter.querySelector('[data-current]');
  const totalEl = counter.querySelector('[data-total]');

  if (!currentEl || !totalEl) {
    return;
  }

  const visibleItems = carousel.visibleItems;
  const total = visibleItems.length;
  const current = Math.max(1, visibleItems.indexOf(carousel.selectedSlide) + 1);

  currentEl.textContent = String(current);
  totalEl.textContent = String(total);
  counter.hidden = total < 2;
}

function initGalleryCounters() {
  document.querySelectorAll('product-gallery').forEach((gallery) => {
    const carousel = gallery.querySelector('media-carousel');
    const counters = gallery.querySelectorAll('[data-gallery-counter]');

    if (!carousel || counters.length === 0) {
      return;
    }

    counters.forEach((counter) => {
      if (counter.dataset.bound === 'true') {
        return;
      }

      counter.dataset.bound = 'true';

      const sync = () => updateGalleryCounter(carousel, counter);

      carousel.addEventListener('carousel:select', sync);
      carousel.addEventListener('carousel:settle', sync);
      carousel.addEventListener('carousel:filter', () => requestAnimationFrame(sync));
      sync();
    });
  });
}

document.addEventListener('DOMContentLoaded', initGalleryCounters);
document.addEventListener('shopify:section:load', initGalleryCounters);
document.addEventListener('product:rerender', () => {
  requestAnimationFrame(initGalleryCounters);
});

const GALLERY_VIDEO_SLIDE_TYPE = 'gallery-video';
const attachedGalleryCarousels = new WeakSet();

function getInlineGalleryVideo(mediaEl) {
  return mediaEl?.querySelector('video-media');
}

function pauseGallerySlideVideos(mediaEl) {
  mediaEl?.querySelectorAll('video-media').forEach((videoMedia) => {
    videoMedia.pause();
    videoMedia.removeAttribute('playing');

    const video = videoMedia.querySelector('video');

    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  });
}

function playGallerySlideVideo(mediaEl) {
  const videoMedia = getInlineGalleryVideo(mediaEl);

  if (!videoMedia) {
    return;
  }

  // External iframe players steal pointer events and break carousel navigation.
  // Keep the poster/sizer visible for those and only autoplay native MP4 videos.
  if (videoMedia.hasAttribute('host')) {
    return;
  }

  const nativeVideo = videoMedia.querySelector('video');

  if (!nativeVideo) {
    return;
  }

  nativeVideo.muted = true;
  nativeVideo.playsInline = true;
  nativeVideo.controls = false;
  nativeVideo.tabIndex = -1;
  videoMedia.setAttribute('loaded', '');
  nativeVideo.play().catch(() => {});
}

function getActiveGallerySlide(carousel) {
  if (carousel.selectedSlide && !carousel.selectedSlide.hidden) {
    return carousel.selectedSlide;
  }

  const center = carousel.getBoundingClientRect().left + (carousel.getBoundingClientRect().width / 2);
  let activeSlide = null;
  let closestDistance = Infinity;

  carousel.querySelectorAll('.product-gallery__media:not([hidden])').forEach((slide) => {
    const rect = slide.getBoundingClientRect();

    if (!rect.width) {
      return;
    }

    const distance = Math.abs(rect.left + (rect.width / 2) - center);

    if (distance < closestDistance) {
      closestDistance = distance;
      activeSlide = slide;
    }
  });

  return activeSlide;
}

function syncGalleryVideoPlayback(carousel) {
  if (!carousel.querySelector('.product-gallery__video-media--inline-autoplay')) {
    return;
  }

  const activeSlide = getActiveGallerySlide(carousel);

  carousel.querySelectorAll('.product-gallery__media[data-media-type="video"], .product-gallery__media[data-media-type="external_video"]').forEach((slide) => {
    if (slide === activeSlide) {
      playGallerySlideVideo(slide);
    } else {
      pauseGallerySlideVideos(slide);
    }
  });
}

function attachGalleryVideoAutoplay(carousel) {
  if (attachedGalleryCarousels.has(carousel)) {
    syncGalleryVideoPlayback(carousel);
    return;
  }

  attachedGalleryCarousels.add(carousel);

  let scheduledSync = null;
  const scheduleSync = () => {
    if (scheduledSync) {
      return;
    }

    scheduledSync = requestAnimationFrame(() => {
      scheduledSync = null;
      syncGalleryVideoPlayback(carousel);
    });
  };

  carousel.addEventListener('scroll', scheduleSync, { passive: true });
  carousel.addEventListener('scrollend', scheduleSync);
  carousel.addEventListener('carousel:settle', scheduleSync);
  carousel.addEventListener('carousel:change', scheduleSync);
  scheduleSync();
}

function initProductGalleryVideoAutoplay() {
  document.querySelectorAll('media-carousel.product-gallery__media-list').forEach(attachGalleryVideoAutoplay);
}

if (!document.documentElement.dataset.galleryVideoAutoplayBound) {
  document.documentElement.dataset.galleryVideoAutoplayBound = 'true';
  document.addEventListener('carousel:settle', (event) => {
    if (event.target.matches('.product-gallery__media-list')) {
      syncGalleryVideoPlayback(event.target);
    }
  });
}

function getVisibleLightboxMedia(gallery) {
  return Array.from(gallery.querySelectorAll('.product-gallery__media:not([hidden])'));
}

function getLightboxIndexForMedia(gallery, mediaEl) {
  if (!mediaEl) {
    return 0;
  }

  return getVisibleLightboxMedia(gallery).indexOf(mediaEl);
}

function getLightboxDimensions(mediaEl) {
  const image = mediaEl.querySelector('img');
  const width = parseInt(image?.getAttribute('width'), 10) || 1920;
  const height = parseInt(image?.getAttribute('height'), 10) || Math.round((width * 9) / 16);

  return { width, height };
}

function buildLightboxDataSource(gallery) {
  return getVisibleLightboxMedia(gallery).map((mediaEl) => {
    const mediaType = mediaEl.getAttribute('data-media-type');
    const { width, height } = getLightboxDimensions(mediaEl);

    if (mediaType === 'image') {
      const image = mediaEl.querySelector('img');

      if (!image) {
        return null;
      }

      return {
        mediaType: 'image',
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width,
        height,
        alt: image.alt,
        thumbCropped: true,
      };
    }

    if (mediaType === 'video' || mediaType === 'external_video') {
      const previewImage = mediaEl.querySelector('img');

      return {
        mediaType,
        type: GALLERY_VIDEO_SLIDE_TYPE,
        itemElement: mediaEl,
        thumbnailElement: previewImage,
        msrc: previewImage?.currentSrc || previewImage?.src,
        width,
        height,
        alt: previewImage?.alt || '',
      };
    }

    return null;
  }).filter(Boolean);
}

function pauseLightboxVideos(root) {
  root?.querySelectorAll('.product-gallery-lightbox__video video-media').forEach((videoMedia) => {
    videoMedia.pause();

    const video = videoMedia.querySelector('video');

    if (video) {
      video.currentTime = 0;
    }
  });
}

function patchPhotoswipeForVideo(photoswipe) {
  if (photoswipe._galleryVideoPatched) {
    return;
  }

  photoswipe._galleryVideoPatched = true;

  photoswipe.on('contentLoad', (event) => {
    const { content } = event;

    if (content.data?.type !== GALLERY_VIDEO_SLIDE_TYPE) {
      return;
    }

    event.preventDefault();

    const sourceVideoMedia = content.data.itemElement?.querySelector('video-media');

    if (!sourceVideoMedia) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'product-gallery-lightbox__video';
    wrapper.appendChild(prepareMediaGridLightboxVideoMedia(sourceVideoMedia));
    content.element = wrapper;
    content.onLoaded();
  });

  photoswipe.on('change', () => {
    pauseLightboxVideos(photoswipe.pswp?.element);

    const content = photoswipe.pswp?.currSlide?.content;

    if (content?.data?.type === GALLERY_VIDEO_SLIDE_TYPE) {
      scheduleMediaGridLightboxVideoPlay(content.element);
    }
  });

  photoswipe.on('contentActivate', ({ content }) => {
    if (content.data?.type === GALLERY_VIDEO_SLIDE_TYPE) {
      scheduleMediaGridLightboxVideoPlay(content.element);
    }
  });

  photoswipe.on('openingAnimationEnd', () => {
    const content = photoswipe.pswp?.currSlide?.content;

    if (content?.data?.type === GALLERY_VIDEO_SLIDE_TYPE) {
      scheduleMediaGridLightboxVideoPlay(content.element);
    }
  });

  photoswipe.on('close', () => {
    pauseLightboxVideos(photoswipe.pswp?.element);
  });
}

function initProductGalleryVideoLightbox() {
  const ProductGallery = customElements.get('product-gallery');

  if (!ProductGallery || ProductGallery.prototype._videoLightboxInitialized) {
    return;
  }

  ProductGallery.prototype._videoLightboxInitialized = true;

  ProductGallery.prototype.openZoom = function(index = 0) {
    patchPhotoswipeForVideo(this.photoswipe);
    this.photoswipe.loadAndOpen(index, buildLightboxDataSource(this));
  };

  try {
    const ProductZoomButton = document.createElement('button', { is: 'product-zoom-button' }).constructor;

    ProductZoomButton.prototype._onButtonClicked = function() {
      const gallery = this.closest('product-gallery');

      if (!gallery?.hasAttribute('allow-zoom')) {
        return;
      }

      const media = this.closest('.product-gallery__media');
      let openIndex;

      if (media) {
        openIndex = getLightboxIndexForMedia(gallery, media);
      } else {
        const carousel = this.closest('.product-gallery__media-list-wrapper')?.querySelector('media-carousel');
        openIndex = getLightboxIndexForMedia(gallery, carousel?.selectedSlide);
      }

      gallery.openZoom(Math.max(0, openIndex));
    };
  } catch (_) {
    // product-zoom-button is optional on some pages
  }

  const MediaCarousel = customElements.get('media-carousel');

  if (MediaCarousel) {
    const originalGestureChanged = MediaCarousel.prototype._onGestureChanged;
    const originalSelect = MediaCarousel.prototype.select;
    const originalTransitionTo = MediaCarousel.prototype._transitionTo;
    const originalOnCarouselScroll = MediaCarousel.prototype._onCarouselScroll;

    MediaCarousel.prototype.select = function(index, { animate = true, force = false } = {}) {
      if (this.getAttribute('desktop-mode') !== 'carousel_split' || !window.matchMedia('(min-width: 1000px)').matches) {
        return originalSelect.call(this, index, { animate, force });
      }

      if (!this.offsetParent) {
        this._selectedIndex = index;
        return;
      }

      const visibleIndex = this.visibleItems.indexOf(this.items[index]);

      if (visibleIndex < 0) {
        return;
      }

      const indexBeforeChange = this.selectedIndex;
      this._selectedIndex = this.items.indexOf(this.visibleItems[visibleIndex]);
      this._dispatchEvent('carousel:select', this._selectedIndex);

      if (this._selectedIndex !== indexBeforeChange || force) {
        this._dispatchEvent('carousel:change', this._selectedIndex);
        this._transitionTo(this.items[indexBeforeChange], this.items[this._selectedIndex], { animate });
      }
    };

    MediaCarousel.prototype._transitionTo = function(fromSlide, toSlide, { animate = true } = {}) {
      if (this.getAttribute('desktop-mode') !== 'carousel_split' || !window.matchMedia('(min-width: 1000px)').matches) {
        return originalTransitionTo.call(this, fromSlide, toSlide, { animate });
      }

      fromSlide?.classList.remove('is-selected');
      toSlide?.classList.add('is-selected');

      const scrollPadding = parseFloat(getComputedStyle(this).scrollPaddingInlineStart) || 0;

      this._hasPendingProgrammaticScroll = animate;
      this.scrollTo({
        left: Math.max(0, toSlide.offsetLeft - scrollPadding),
        behavior: animate ? 'smooth' : 'auto',
      });
    };

    MediaCarousel.prototype._onCarouselScroll = function() {
      if (this.getAttribute('desktop-mode') !== 'carousel_split' || !window.matchMedia('(min-width: 1000px)').matches) {
        return originalOnCarouselScroll.call(this);
      }

      if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === 'none') {
        return;
      }

      const scrollPadding = parseFloat(getComputedStyle(this).scrollPaddingInlineStart) || 0;
      const target = this.scrollLeft + scrollPadding;
      let closest = null;
      let closestDistance = Infinity;

      this.visibleItems.forEach((item) => {
        const distance = Math.abs(item.offsetLeft - target);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = item;
        }
      });

      if (!closest) {
        return;
      }

      const newIndex = this.items.indexOf(closest);

      if (newIndex !== this.selectedIndex) {
        this._selectedIndex = newIndex;
        this._dispatchEvent('carousel:select', this.selectedIndex);
        this._dispatchEvent('carousel:change', this.selectedIndex);
      }
    };

    MediaCarousel.prototype._onMediaSettled = function(event) {
      const media = event.detail.slide;

      this.items
        .filter((item) => ['video', 'external_video', 'model'].includes(item.getAttribute('data-media-type')))
        .forEach((item) => {
          item.querySelector('video-media, model-media')?.pause?.();
        });

      switch (media.getAttribute('data-media-type')) {
        case 'external_video':
        case 'video':
          if (this.hasAttribute('autoplay')) {
            playGallerySlideVideo(media);
          }
          break;
        case 'model':
          if (window.matchMedia('(min-width: 1000px)').matches) {
            media.querySelector('model-media')?.play?.();
          }
          break;
      }
    };

    MediaCarousel.prototype._onGestureChanged = function(event) {
      const gallery = this.closest('product-gallery');

      if (!gallery?.hasAttribute('allow-zoom')) {
        return originalGestureChanged.call(this, event);
      }

      event.preventDefault();

      if (event.scale > 1.5) {
        const openIndex = getLightboxIndexForMedia(gallery, this.selectedSlide);
        this.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index: openIndex } }));
        this.removeEventListener('gesturechange', this._onGestureChangedListener);
      }
    };

    MediaCarousel.prototype._onGalleryClick = function(event) {
      const gallery = this.closest('product-gallery[allow-zoom]');

      // Image tap/click opens the lightbox (all viewports); skip carousel half-click nav.
      if (gallery) {
        return;
      }

      if (this.getAttribute('desktop-mode') === 'carousel_split' && window.matchMedia('(min-width: 1000px)').matches) {
        return;
      }

      if (event.target.matches('button, a[href], button :scope, a[href] :scope') || !window.matchMedia('screen and (pointer: fine)').matches) {
        return;
      }

      if (this.selectedSlide?.getAttribute('data-media-type') === 'model') {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;

      offsetX > this.clientWidth / 2 ? this.next() : this.previous();
    };
  }
}

const GALLERY_DESKTOP_LIGHTBOX_MEDIA_TYPES = new Set(['image', 'video', 'external_video']);
const GALLERY_LIGHTBOX_TAP_THRESHOLD_PX = 10;

function bindProductGalleryDesktopLightbox() {
  if (document.documentElement.dataset.galleryDesktopLightboxBound) {
    return;
  }

  document.documentElement.dataset.galleryDesktopLightboxBound = 'true';

  const desktopMediaQuery = window.matchMedia('(min-width: 1000px)');
  let galleryPointer = null;

  const resetGalleryPointer = () => {
    galleryPointer = null;
  };

  const startGalleryPointer = (clientX, clientY, mediaEl) => {
    galleryPointer = {
      mediaEl,
      startX: clientX,
      startY: clientY,
      moved: false,
    };
  };

  const updateGalleryPointer = (clientX, clientY) => {
    if (!galleryPointer || galleryPointer.moved) {
      return;
    }

    const deltaX = Math.abs(clientX - galleryPointer.startX);
    const deltaY = Math.abs(clientY - galleryPointer.startY);

    if (deltaX > GALLERY_LIGHTBOX_TAP_THRESHOLD_PX || deltaY > GALLERY_LIGHTBOX_TAP_THRESHOLD_PX) {
      galleryPointer.moved = true;
    }
  };

  document.addEventListener('pointerdown', (event) => {
    const mediaEl = event.target.closest?.('.product-gallery__media');

    if (!mediaEl || mediaEl.hasAttribute('hidden') || !mediaEl.closest('product-gallery[allow-zoom]')) {
      resetGalleryPointer();
      return;
    }

    if (event.target.closest('button, a[href]')) {
      resetGalleryPointer();
      return;
    }

    startGalleryPointer(event.clientX, event.clientY, mediaEl);
  }, true);

  document.addEventListener('pointermove', (event) => {
    updateGalleryPointer(event.clientX, event.clientY);
  }, true);

  document.addEventListener('pointercancel', resetGalleryPointer, true);
  document.addEventListener('pointerup', () => {
    // Keep pointer state until click so we can ignore post-swipe clicks.
    window.setTimeout(resetGalleryPointer, 0);
  }, true);

  document.addEventListener('click', (event) => {
    const mediaEl = event.target.closest('.product-gallery__media');

    if (!mediaEl || mediaEl.hasAttribute('hidden')) {
      return;
    }

    const gallery = mediaEl.closest('product-gallery[allow-zoom]');

    if (!gallery) {
      return;
    }

    const carousel = mediaEl.closest('media-carousel');
    const isDesktop = desktopMediaQuery.matches;

    // Split gallery keeps desktop click-to-navigate (arrows / left-right halves).
    if (isDesktop && carousel?.getAttribute('desktop-mode') === 'carousel_split') {
      return;
    }

    const mediaType = mediaEl.getAttribute('data-media-type');

    // Desktop: images + videos. Mobile: images only (videos keep native play controls).
    if (isDesktop) {
      if (!GALLERY_DESKTOP_LIGHTBOX_MEDIA_TYPES.has(mediaType)) {
        return;
      }
    } else if (mediaType !== 'image') {
      return;
    }

    if (event.target.closest('button, a[href]')) {
      return;
    }

    // Ignore clicks that followed a swipe/drag on the gallery carousel.
    if (galleryPointer?.moved && galleryPointer.mediaEl === mediaEl) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    gallery.openZoom(Math.max(0, getLightboxIndexForMedia(gallery, mediaEl)));
  }, true);
}

function syncProductGalleryCursor(gallery) {
  const cursor = gallery?.querySelector('.product-gallery__cursor');

  if (!cursor) {
    return;
  }

  const selectedSlide = gallery.querySelector('media-carousel')?.selectedSlide
    || gallery.querySelector('.product-gallery__media.is-selected');
  const mediaType = selectedSlide?.getAttribute('data-media-type');

  cursor.toggleAttribute('hidden', mediaType === 'model');
}

function bindProductGalleryCursorForVideos() {
  if (document.documentElement.dataset.galleryCursorVideoBound) {
    return;
  }

  document.documentElement.dataset.galleryCursorVideoBound = 'true';

  // Theme hides the cursor on non-image slides; re-enable it for videos after that runs.
  document.addEventListener('carousel:change', (event) => {
    const carousel = event.target;

    if (!carousel?.matches?.('media-carousel.product-gallery__media-list')) {
      return;
    }

    const cursor = carousel.closest('product-gallery')?.querySelector('.product-gallery__cursor');

    if (!cursor || !event.detail?.slide) {
      return;
    }

    cursor.toggleAttribute('hidden', event.detail.slide.getAttribute('data-media-type') === 'model');
  });
}

function bindSplitGalleryClickNav() {
  if (document.documentElement.dataset.splitGalleryClickNavBound) {
    return;
  }

  document.documentElement.dataset.splitGalleryClickNavBound = 'true';

  document.addEventListener('click', (event) => {
    if (!window.matchMedia('(min-width: 1000px) and (pointer: fine)').matches) {
      return;
    }

    const carousel = event.target.closest('media-carousel[desktop-mode="carousel_split"]');

    if (!carousel || event.target.closest('button, a[href], .product-info')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    carousel.focus({ preventScroll: true });

    const offsetX = event.clientX - carousel.getBoundingClientRect().left;
    offsetX > carousel.clientWidth / 2 ? carousel.next() : carousel.previous();
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    if (!window.matchMedia('(min-width: 1000px)').matches) {
      return;
    }

    if (event.target.closest('input, textarea, select, [contenteditable="true"]')) {
      return;
    }

    const gallery = document.querySelector('.product--split-overlay product-gallery.product-gallery--desktop-split');
    const carousel = gallery?.querySelector('media-carousel[desktop-mode="carousel_split"]');

    if (!gallery || !carousel) {
      return;
    }

    const galleryIsActive = gallery.matches(':hover') || gallery.contains(document.activeElement);

    if (!galleryIsActive) {
      return;
    }

    event.preventDefault();
    event.key === 'ArrowLeft' ? carousel.previous() : carousel.next();
  }, true);
}

const SPLIT_CONTROLS_LUMINANCE_THRESHOLD = 0.58;
const splitControlsImageCache = new Map();

function getRelativeLuminance(r, g, b) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function loadCorsImage(src) {
  if (splitControlsImageCache.has(src)) {
    return splitControlsImageCache.get(src);
  }

  const promise = new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  }).catch(() => null);

  splitControlsImageCache.set(src, promise);
  return promise;
}

function getObjectFitCoverSourceRect(naturalWidth, naturalHeight, elementWidth, elementHeight, localX, localY, sampleSize) {
  const scale = Math.max(elementWidth / naturalWidth, elementHeight / naturalHeight);
  const drawnWidth = naturalWidth * scale;
  const drawnHeight = naturalHeight * scale;
  const offsetX = (elementWidth - drawnWidth) / 2;
  const offsetY = (elementHeight - drawnHeight) / 2;
  const half = sampleSize / 2;

  let sourceX = ((localX - offsetX) / scale) - half;
  let sourceY = ((localY - offsetY) / scale) - half;

  sourceX = Math.max(0, Math.min(Math.max(0, naturalWidth - sampleSize), sourceX));
  sourceY = Math.max(0, Math.min(Math.max(0, naturalHeight - sampleSize), sourceY));

  return {
    x: sourceX,
    y: sourceY,
    width: Math.min(sampleSize, naturalWidth),
    height: Math.min(sampleSize, naturalHeight),
  };
}

async function sampleMediaLuminance(mediaEl, clientX, clientY) {
  const image = mediaEl.querySelector('img.product-gallery__media-sizer, img');
  const video = mediaEl.querySelector('video');
  const source = image?.naturalWidth ? image : (video?.videoWidth ? video : image);

  if (!source) {
    return null;
  }

  const rect = source.getBoundingClientRect();

  if (rect.width < 1 || rect.height < 1) {
    return null;
  }

  const localX = clientX - rect.left;
  const localY = clientY - rect.top;
  const sampleSize = 16;
  const canvas = document.createElement('canvas');
  canvas.width = sampleSize;
  canvas.height = sampleSize;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  try {
    if (source instanceof HTMLVideoElement) {
      const sourceRect = getObjectFitCoverSourceRect(
        source.videoWidth,
        source.videoHeight,
        rect.width,
        rect.height,
        localX,
        localY,
        sampleSize,
      );

      context.drawImage(
        source,
        sourceRect.x,
        sourceRect.y,
        sourceRect.width,
        sourceRect.height,
        0,
        0,
        sampleSize,
        sampleSize,
      );
    } else {
      const src = source.currentSrc || source.src;

      if (!src) {
        return null;
      }

      const corsImage = await loadCorsImage(src);

      if (!corsImage) {
        return null;
      }

      const sourceRect = getObjectFitCoverSourceRect(
        corsImage.naturalWidth,
        corsImage.naturalHeight,
        rect.width,
        rect.height,
        localX,
        localY,
        sampleSize,
      );

      context.drawImage(
        corsImage,
        sourceRect.x,
        sourceRect.y,
        sourceRect.width,
        sourceRect.height,
        0,
        0,
        sampleSize,
        sampleSize,
      );
    }
  } catch (_) {
    return null;
  }

  const pixels = context.getImageData(0, 0, sampleSize, sampleSize).data;
  let total = 0;
  let count = 0;

  for (let index = 0; index < pixels.length; index += 4) {
    if (pixels[index + 3] < 16) {
      continue;
    }

    total += getRelativeLuminance(pixels[index], pixels[index + 1], pixels[index + 2]);
    count += 1;
  }

  return count ? total / count : null;
}

async function updateControlContrast(target) {
  const rect = target.getBoundingClientRect();

  if (rect.width < 1 || rect.height < 1) {
    return;
  }

  const sampleX = rect.left + (rect.width / 2);
  const sampleY = rect.top + (rect.height / 2);
  const previousPointerEvents = target.style.pointerEvents;

  target.style.pointerEvents = 'none';
  const element = document.elementFromPoint(sampleX, sampleY);
  target.style.pointerEvents = previousPointerEvents;

  const mediaEl = element?.closest?.('.product-gallery__media');

  if (!mediaEl) {
    target.classList.remove('is-on-dark');
    return;
  }

  const luminance = await sampleMediaLuminance(mediaEl, sampleX, sampleY);

  if (luminance == null) {
    return;
  }

  target.classList.toggle('is-on-dark', luminance < SPLIT_CONTROLS_LUMINANCE_THRESHOLD);
}

async function updateSplitControlsContrast(controls) {
  if (!window.matchMedia('(min-width: 1000px)').matches) {
    controls.classList.remove('is-on-dark');
    return;
  }

  await updateControlContrast(controls);
}

async function updateMobileFreeScrollControlsContrast(controls) {
  if (!window.matchMedia('(max-width: 999px)').matches) {
    controls.querySelector('.product-gallery__mobile-counter')?.classList.remove('is-on-dark');
    return;
  }

  const counter = controls.querySelector('.product-gallery__mobile-counter');

  if (!counter) {
    return;
  }

  await updateControlContrast(counter);
}

function bindGalleryControlsContrast(controls, updateFn) {
  if (controls.dataset.contrastBound === 'true') {
    return;
  }

  controls.dataset.contrastBound = 'true';

  const gallery = controls.closest('product-gallery');
  const carousel = gallery?.querySelector('media-carousel');
  let frame = null;

  const scheduleUpdate = () => {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(() => {
      frame = null;
      updateFn(controls);
    });
  };

  carousel?.addEventListener('scroll', scheduleUpdate, { passive: true });
  carousel?.addEventListener('carousel:settle', scheduleUpdate);
  carousel?.addEventListener('carousel:change', scheduleUpdate);
  carousel?.addEventListener('carousel:select', scheduleUpdate);
  window.addEventListener('resize', scheduleUpdate);
  gallery?.querySelectorAll('img').forEach((image) => {
    if (!image.complete) {
      image.addEventListener('load', scheduleUpdate, { once: true });
    }
  });

  scheduleUpdate();
}

function initSplitControlsContrast() {
  document.querySelectorAll('.product--split-overlay .product-gallery__split-controls').forEach((controls) => {
    bindGalleryControlsContrast(controls, updateSplitControlsContrast);
  });

  document.querySelectorAll('.product-gallery--mobile-free-scroll-controls .product-gallery__mobile-controls').forEach((controls) => {
    bindGalleryControlsContrast(controls, updateMobileFreeScrollControlsContrast);
  });
}

function initSplitGalleryCursorHint() {
  const storageKey = 'argonSplitGalleryCursorHintDismissed';

  document.querySelectorAll('.product--split-overlay .product-gallery__media-list-wrapper').forEach((wrapper) => {
    const cursor = wrapper.querySelector('.product-gallery__cursor');

    if (!cursor?.querySelector('.product-gallery__cursor-hint') || wrapper.dataset.cursorHintBound === 'true') {
      return;
    }

    wrapper.dataset.cursorHintBound = 'true';

    let dismissed = false;

    try {
      dismissed = sessionStorage.getItem(storageKey) === '1';
    } catch (_) {
      dismissed = false;
    }

    if (dismissed) {
      cursor.classList.add('is-hint-dismissed');
      cursor.classList.remove('is-hint-enabled');
      return;
    }

    cursor.classList.add('is-hint-enabled');

    const dismiss = () => {
      dismissed = true;
      cursor.classList.add('is-hint-dismissed');
      cursor.classList.remove('is-hint-enabled');

      try {
        sessionStorage.setItem(storageKey, '1');
      } catch (_) {
        // ignore
      }
    };

    const reveal = () => {
      if (dismissed || !window.matchMedia('(min-width: 1000px) and (pointer: fine)').matches) {
        return;
      }

      cursor.classList.add('is-hint-enabled');
      cursor.classList.remove('is-hint-dismissed');
    };

    wrapper.addEventListener('pointerenter', reveal);
    wrapper.addEventListener('pointerleave', () => {
      if (!dismissed) {
        cursor.classList.add('is-hint-dismissed');
      }
    });
    // pointerdown + capture: gallery click nav stops click propagation.
    wrapper.addEventListener('pointerdown', dismiss, true);
  });
}

function initSplitProductInfoScrollHint() {
  document.querySelectorAll('.product--split-overlay .product-info-scroll').forEach((wrapper) => {
    const info = wrapper.querySelector('.product-info');

    if (!info || wrapper.dataset.scrollHintBound === 'true') {
      return;
    }

    wrapper.dataset.scrollHintBound = 'true';

    const updateScrollState = () => {
      const canScroll = info.scrollHeight - info.clientHeight > 1;
      const atBottom = info.scrollTop + info.clientHeight >= info.scrollHeight - 2;
      const atTop = info.scrollTop <= 2;

      wrapper.classList.toggle('is-scrollable', canScroll);
      wrapper.classList.toggle('is-at-bottom', atBottom);
      wrapper.classList.toggle('is-at-top', atTop);
    };

    info.addEventListener('scroll', updateScrollState, { passive: true });
    new ResizeObserver(updateScrollState).observe(info);

    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(updateScrollState).observe(info, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    updateScrollState();
  });
}

function bootProductGalleryVideoFeatures() {
  bindProductGalleryCursorForVideos();
  bindSplitGalleryClickNav();
  bindProductGalleryDesktopLightbox();
  initProductGalleryVideoAutoplay();
  initSplitControlsContrast();
  initSplitGalleryCursorHint();
  initSplitProductInfoScrollHint();
  document.querySelectorAll('product-gallery').forEach(syncProductGalleryCursor);
}

initProductGalleryVideoLightbox();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductGalleryVideoFeatures);
} else {
  bootProductGalleryVideoFeatures();
}

document.addEventListener('shopify:section:load', bootProductGalleryVideoFeatures);
document.addEventListener('product:rerender', () => requestAnimationFrame(bootProductGalleryVideoFeatures));
window.addEventListener('load', bootProductGalleryVideoFeatures);

const MEDIA_GRID_VIDEO_SLIDE_TYPE = 'media-grid-video';

function getMediaGridLightboxDimensions(item) {
  const mediaType = item.getAttribute('data-media-type');
  const video = item.querySelector('video');
  const image = item.querySelector('.content-over-media__media') || item.querySelector('img');

  if (mediaType === 'video' || mediaType === 'external_video') {
    if (video?.videoWidth > 0 && video?.videoHeight > 0) {
      return { width: video.videoWidth, height: video.videoHeight };
    }

    const posterWidth = parseInt(image?.getAttribute('width'), 10);
    const posterHeight = parseInt(image?.getAttribute('height'), 10);

    if (posterWidth && posterHeight) {
      return { width: posterWidth, height: posterHeight };
    }

    return { width: 1920, height: 1080 };
  }

  const width = parseInt(image?.getAttribute('width'), 10) || 1920;
  const height = parseInt(image?.getAttribute('height'), 10) || Math.round((width * 9) / 16);

  return { width, height };
}

function buildMediaGridLightboxDataSource(grid) {
  return Array.from(grid.querySelectorAll('[data-lightbox-item]')).map((item) => {
    const mediaType = item.getAttribute('data-media-type');
    const { width, height } = getMediaGridLightboxDimensions(item);

    if (mediaType === 'image') {
      const image = item.querySelector('img');

      if (!image) {
        return null;
      }

      return {
        mediaType,
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width,
        height,
        alt: image.alt,
        thumbCropped: true,
      };
    }

    if (mediaType === 'video' || mediaType === 'external_video') {
      const previewImage = item.querySelector('img');

      return {
        mediaType,
        type: MEDIA_GRID_VIDEO_SLIDE_TYPE,
        itemElement: item,
        thumbnailElement: previewImage,
        msrc: previewImage?.currentSrc || previewImage?.src,
        width,
        height,
        alt: previewImage?.alt || '',
      };
    }

    return null;
  }).filter(Boolean);
}

function getLightboxVideoAspectRatio(sourceVideoMedia) {
  const sourceVideo = sourceVideoMedia.querySelector('video');
  const posterImage = sourceVideoMedia.querySelector('img') || sourceVideo?.querySelector('img');

  if (sourceVideo?.videoWidth > 0 && sourceVideo?.videoHeight > 0) {
    return `${sourceVideo.videoWidth} / ${sourceVideo.videoHeight}`;
  }

  const posterWidth = parseInt(posterImage?.getAttribute('width'), 10);
  const posterHeight = parseInt(posterImage?.getAttribute('height'), 10);

  if (posterWidth && posterHeight) {
    return `${posterWidth} / ${posterHeight}`;
  }

  return '16 / 9';
}

function getLightboxExternalVideoSrc(sourceVideoMedia) {
  const iframe = sourceVideoMedia.querySelector('iframe');
  const templateIframe = sourceVideoMedia.querySelector('template')?.content.querySelector('iframe');
  const iframeSrc = iframe?.src || templateIframe?.getAttribute('src');

  if (!iframeSrc) {
    return null;
  }

  const url = new URL(iframeSrc, window.location.href);

  url.searchParams.set('controls', '1');
  url.searchParams.set('autoplay', '1');
  url.searchParams.set('mute', '1');
  url.searchParams.set('muted', '1');
  url.searchParams.set('playsinline', '1');

  if (url.hostname.includes('vimeo')) {
    url.searchParams.set('background', '0');
    url.searchParams.set('autopause', '0');
  }

  return url.toString();
}

function playMediaGridLightboxVideo(wrapper) {
  const videoMedia = wrapper?.querySelector('video-media');

  if (!videoMedia) {
    return;
  }

  const video = videoMedia.querySelector('video');

  if (video) {
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
  }

  videoMedia.play({ restart: true });
}

function scheduleMediaGridLightboxVideoPlay(wrapper) {
  if (!wrapper) {
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      playMediaGridLightboxVideo(wrapper);
    });
  });
}

function prepareMediaGridLightboxVideoMedia(sourceVideoMedia) {
  const host = sourceVideoMedia.getAttribute('host');
  const videoMedia = document.createElement('video-media');
  videoMedia.setAttribute('autoplay', '');
  videoMedia.style.setProperty('--aspect-ratio', getLightboxVideoAspectRatio(sourceVideoMedia));

  if (host) {
    videoMedia.setAttribute('host', host);

    const iframeSrc = getLightboxExternalVideoSrc(sourceVideoMedia);

    if (iframeSrc) {
      const template = document.createElement('template');
      const iframe = document.createElement('iframe');
      iframe.src = iframeSrc;
      iframe.setAttribute('allow', 'autoplay; encrypted-media');
      iframe.setAttribute('allowfullscreen', 'allowfullscreen');
      template.content.append(iframe);
      videoMedia.appendChild(template);
    }

    return videoMedia;
  }

  const sourceVideo = sourceVideoMedia.querySelector('video');

  if (!sourceVideo) {
    return sourceVideoMedia.cloneNode(true);
  }

  const video = document.createElement('video');
  video.setAttribute('playsinline', '');
  video.setAttribute('controls', '');
  video.setAttribute('preload', 'auto');
  video.setAttribute('muted', '');
  video.setAttribute('autoplay', '');
  video.muted = true;
  video.autoplay = true;

  if (sourceVideo.poster) {
    video.poster = sourceVideo.poster;
  }

  if (sourceVideo.currentSrc) {
    video.src = sourceVideo.currentSrc;
  } else {
    sourceVideo.querySelectorAll('source').forEach((source) => {
      video.appendChild(source.cloneNode(true));
    });
  }

  videoMedia.appendChild(video);
  return videoMedia;
}

function pauseMediaGridLightboxVideos(root) {
  root?.querySelectorAll('.product-gallery-lightbox__video video-media').forEach((videoMedia) => {
    videoMedia.pause();

    const video = videoMedia.querySelector('video');

    if (video) {
      video.currentTime = 0;
    }
  });
}

function playActiveMediaGridLightboxVideo(pswp) {
  const content = pswp?.currSlide?.content;

  if (content?.data?.type !== MEDIA_GRID_VIDEO_SLIDE_TYPE) {
    return;
  }

  scheduleMediaGridLightboxVideoPlay(content.element);
}

function patchMediaGridPhotoswipeForVideo(photoswipe, grid) {
  if (photoswipe._mediaGridVideoPatched) {
    return;
  }

  photoswipe._mediaGridVideoPatched = true;
  photoswipe._mediaGridElement = grid;

  photoswipe.on('contentLoad', (event) => {
    const { content } = event;

    if (content.data?.type !== MEDIA_GRID_VIDEO_SLIDE_TYPE) {
      return;
    }

    event.preventDefault();

    const sourceVideoMedia = content.data.itemElement?.querySelector('video-media');

    if (!sourceVideoMedia) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'product-gallery-lightbox__video';
    wrapper.appendChild(prepareMediaGridLightboxVideoMedia(sourceVideoMedia));
    content.element = wrapper;
    content.onLoaded();
  });

  photoswipe.on('contentActivate', ({ content }) => {
    if (content.data?.type !== MEDIA_GRID_VIDEO_SLIDE_TYPE) {
      return;
    }

    scheduleMediaGridLightboxVideoPlay(content.element);
  });

  photoswipe.on('close', () => {
    pauseMediaGridLightboxVideos(photoswipe.pswp?.element);

    photoswipe._mediaGridElement?.querySelectorAll('video-media[autoplay]').forEach((videoMedia) => videoMedia.play());
  });
}

async function getMediaGridPhotoswipe(grid) {
  if (grid._photoswipe) {
    return grid._photoswipe;
  }

  const { PhotoSwipeLightbox } = await import('vendor');
  const photoswipe = new PhotoSwipeLightbox({
    pswpModule: () => import('photoswipe'),
    bgOpacity: 1,
    maxZoomLevel: 3,
    closeTitle: window.themeVariables.strings.closeGallery,
    zoomTitle: window.themeVariables.strings.zoomGallery,
    errorMsg: window.themeVariables.strings.errorGallery,
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    zoom: false,
    closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
      <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
      <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
      <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
    </svg>`,
  });

  photoswipe.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);
  patchMediaGridPhotoswipeForVideo(photoswipe, grid);

  photoswipe.on('uiRegister', () => {
    photoswipe.pswp.ui.registerElement({
      name: 'bottom-bar',
      order: 5,
      appendTo: 'wrapper',
      html: `
        <div class="pagination">
          <button class="pagination__item group" rel="prev">
            <span class="animated-arrow animated-arrow--reverse"></span>
          </button>

          <span class="pagination__current text-sm">
            <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
          </span>

          <button class="pagination__item group" rel="next">
            <span class="animated-arrow"></span>
          </button>
        </div>
      `,
      onInit: (el, pswp) => {
        el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
        el.querySelector('[rel="prev"]')?.addEventListener('click', () => pswp.prev());
        el.querySelector('[rel="next"]')?.addEventListener('click', () => pswp.next());
        pswp.on('change', () => {
          el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          pauseMediaGridLightboxVideos(pswp.element);
          playActiveMediaGridLightboxVideo(pswp);
        });
        pswp.on('openingAnimationEnd', () => {
          playActiveMediaGridLightboxVideo(pswp);
        });
      },
    });
  });

  photoswipe.init();
  grid._photoswipe = photoswipe;
  return photoswipe;
}

async function openMediaGridLightbox(grid, item) {
  const items = Array.from(grid.querySelectorAll('[data-lightbox-item]'));
  const index = Math.max(0, items.indexOf(item));
  const dataSource = buildMediaGridLightboxDataSource(grid);

  if (!dataSource.length) {
    return;
  }

  grid.querySelectorAll('video-media').forEach((videoMedia) => videoMedia.pause());

  const photoswipe = await getMediaGridPhotoswipe(grid);
  photoswipe.loadAndOpen(index, dataSource);
}

const MEDIA_GRID_LIGHTBOX_SELECTOR = 'media-grid[allow-lightbox], .product-metafield-gallery__grid[allow-lightbox]';

function getMediaGridLightboxGrid(element) {
  return element?.closest(MEDIA_GRID_LIGHTBOX_SELECTOR) ?? null;
}

function bootMediaGridLightbox() {
  bindMediaGridLightbox();

  document.querySelectorAll(MEDIA_GRID_LIGHTBOX_SELECTOR).forEach((grid) => {
    getMediaGridPhotoswipe(grid);
  });
}

function bindMediaGridLightbox() {
  if (document.documentElement.dataset.mediaGridLightboxBound) {
    return;
  }

  document.documentElement.dataset.mediaGridLightboxBound = 'true';

  document.addEventListener('click', (event) => {
    const item = event.target.closest('.media-grid__item--lightbox');

    if (!item || event.target.closest('a[href], button')) {
      return;
    }

    const grid = getMediaGridLightboxGrid(item);

    if (!grid) {
      return;
    }

    event.preventDefault();
    openMediaGridLightbox(grid, item);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const item = event.target.closest('.media-grid__item--lightbox[role="button"]');
    const grid = getMediaGridLightboxGrid(item);

    if (!item || !grid) {
      return;
    }

    event.preventDefault();
    openMediaGridLightbox(grid, item);
  });
}

function getProductMetafieldGalleryRevealOrder(items, gallery) {
  const isHorizontal = gallery.classList.contains('product-metafield-gallery--below-info')
    || window.matchMedia('(max-width: 999px)').matches;

  return isHorizontal ? items.slice().reverse() : items;
}

async function revealProductMetafieldGallery(gallery) {
  if (gallery.dataset.revealBound === 'true') {
    return;
  }

  gallery.dataset.revealBound = 'true';

  const grid = gallery.querySelector('.product-metafield-gallery__grid');

  if (!grid) {
    return;
  }

  const items = Array.from(grid.querySelectorAll('.product-metafield-gallery__item'));

  if (!items.length) {
    return;
  }

  if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
    items.forEach((item) => {
      item.style.opacity = '1';
      item.style.transform = 'none';
    });
    return;
  }

  const { animate, stagger, inView } = await import('vendor');

  inView(gallery, () => {
    if (gallery.dataset.revealed === 'true') {
      return;
    }

    gallery.dataset.revealed = 'true';

    const orderedItems = getProductMetafieldGalleryRevealOrder(items, gallery);

    animate(
      orderedItems,
      { opacity: [0, 1], transform: ['translateX(24px)', 'translateX(0)'] },
      { duration: 0.45, delay: stagger(0.1, { easing: 'ease-out' }), easing: 'ease-out' }
    );
  }, { margin: '-50px 0px' });
}

function bootProductMetafieldGalleryReveal() {
  document.querySelectorAll('.product-metafield-gallery:not([data-reveal-init])').forEach((gallery) => {
    gallery.dataset.revealInit = 'true';
    revealProductMetafieldGallery(gallery);
  });
}

function bootProductMetafieldGallery() {
  bootMediaGridLightbox();
  bootProductMetafieldGalleryReveal();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductMetafieldGallery);
} else {
  bootProductMetafieldGallery();
}

document.addEventListener('shopify:section:load', bootProductMetafieldGallery);

class ShippingCountdown extends HTMLElement {
  connectedCallback() {
    const match = this.dataset.cutoffTime?.trim().match(/^(\d{1,2}):(\d{2})$/);

    if (!match) {
      return;
    }

    this._cutoffHours = Number(match[1]);
    this._cutoffMinutes = Number(match[2]);
    this._hoursEl = this.querySelector('.shipping-countdown__hours');
    this._minutesEl = this.querySelector('.shipping-countdown__minutes');
    this._secondsEl = this.querySelector('.shipping-countdown__seconds');

    this._update();
    this._interval = window.setInterval(() => this._update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this._interval);
  }

  _getNextCutoff() {
    const now = new Date();
    const target = new Date();

    target.setHours(this._cutoffHours, this._cutoffMinutes, 0, 0);

    if (now >= target) {
      target.setDate(target.getDate() + 1);
    }

    return target;
  }

  _update() {
    const now = new Date();
    const target = this._getNextCutoff();
    const diff = Math.max(0, target - now);
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (this._hoursEl) {
      this._hoursEl.textContent = String(hours).padStart(2, '0');
    }

    if (this._minutesEl) {
      this._minutesEl.textContent = String(minutes).padStart(2, '0');
    }

    if (this._secondsEl) {
      this._secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }
}

if (!window.customElements.get('shipping-countdown')) {
  window.customElements.define('shipping-countdown', ShippingCountdown);
}

function hideEmptyLooxReviewsSection() {
  const reviewSection = document.getElementById('product-customer-review');

  if (!reviewSection) {
    return;
  }

  const iframe = reviewSection.querySelector('#looxReviewsFrame');

  if (!iframe) {
    return;
  }

  const hideIfEmpty = () => {
    const height = parseInt(iframe.getAttribute('height') || iframe.style.height || '0', 10);

    if (height > 0) {
      return;
    }

    const shopifySection = reviewSection.closest('.shopify-section');

    if (shopifySection) {
      shopifySection.hidden = true;
    }
  };

  hideIfEmpty();
  iframe.addEventListener('load', hideIfEmpty, { once: true });

  if (window.ResizeObserver) {
    const resizeObserver = new ResizeObserver(hideIfEmpty);
    resizeObserver.observe(iframe);
  }
}

document.addEventListener('DOMContentLoaded', hideEmptyLooxReviewsSection);
document.addEventListener('shopify:section:load', hideEmptyLooxReviewsSection);

var STOREPOINT_SCRIPT_URL = 'https://cdn.storepoint.co/api/v1/js/160ee952367f27.js';
var STORE_DRAWER_HASH = '#find-store';

function loadStorepointScript() {
  if (document.querySelector('script[src="' + STOREPOINT_SCRIPT_URL + '"]')) {
    return;
  }

  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = STOREPOINT_SCRIPT_URL;
  var firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(script, firstScript);
}

function setStoreDrawerHashInUrl() {
  if (window.location.hash === STORE_DRAWER_HASH) {
    return;
  }

  history.replaceState(null, '', window.location.pathname + window.location.search + STORE_DRAWER_HASH);
}

function clearStoreDrawerHashFromUrl() {
  if (window.location.hash !== STORE_DRAWER_HASH) {
    return;
  }

  history.replaceState(null, '', window.location.pathname + window.location.search);
}

function openStoreDrawerFromUrl() {
  if (window.location.hash !== STORE_DRAWER_HASH) {
    return;
  }

  var drawer = document.getElementById('store-drawer');
  if (!drawer) {
    return;
  }

  loadStorepointScript();

  if (!drawer.open) {
    drawer.show();
  }
}

function initStoreDrawerUrlOpening() {
  openStoreDrawerFromUrl();
  window.addEventListener('hashchange', openStoreDrawerFromUrl);

  var drawer = document.getElementById('store-drawer');
  if (!drawer) {
    return;
  }

  drawer.addEventListener('dialog:after-show', setStoreDrawerHashInUrl);
  drawer.addEventListener('dialog:after-hide', clearStoreDrawerHashFromUrl);
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[aria-controls="store-drawer"]').forEach(function (element) {
    element.addEventListener('click', loadStorepointScript);
  });

  initStoreDrawerUrlOpening();
});

const PRODUCT_DRAWER_SELECTOR = 'x-drawer.product-specifications-drawer, x-drawer.product-usp-drawer';

function isInDocumentTree(element) {
  return element?.getRootNode() === document;
}

function getCanonicalProductDrawer(drawerId) {
  const triggers = document.querySelectorAll(`.product-usp__info-button[aria-controls="${CSS.escape(drawerId)}"]`);

  for (const trigger of triggers) {
    const blockItem = trigger.closest('.product-info__block-item');
    const drawer = blockItem?.querySelector(`${PRODUCT_DRAWER_SELECTOR}[id="${CSS.escape(drawerId)}"]`);

    if (drawer) {
      return drawer;
    }
  }

  return null;
}

function removeProductDrawerElement(drawer) {
  drawer.delegate?.off();

  if (drawer.hasAttribute('open')) {
    drawer.removeAttribute('open');
  }

  drawer.remove();
}

function cleanupDuplicateProductDrawers() {
  const drawersById = new Map();

  document.querySelectorAll(PRODUCT_DRAWER_SELECTOR).forEach((drawer) => {
    if (!drawer.id) {
      return;
    }

    if (!drawersById.has(drawer.id)) {
      drawersById.set(drawer.id, []);
    }

    drawersById.get(drawer.id).push(drawer);
  });

  drawersById.forEach((drawers, id) => {
    if (drawers.length <= 1) {
      return;
    }

    const preferred = getCanonicalProductDrawer(id)
      || drawers.find((drawer) => drawer.closest('.product-info'))
      || drawers[0];

    drawers.forEach((drawer) => {
      if (drawer !== preferred) {
        removeProductDrawerElement(drawer);
      }
    });
  });
}

function patchDialogElementForFragmentDrawers() {
  const dialogPrototype = customElements.get('x-drawer').prototype.__proto__;
  const originalConnectedCallback = dialogPrototype.connectedCallback;
  const originalAttributeChangedCallback = dialogPrototype.attributeChangedCallback;

  dialogPrototype.connectedCallback = function () {
    originalConnectedCallback.call(this);

    if (this.id && !isInDocumentTree(this)) {
      this.delegate.off();
    }
  };

  dialogPrototype.attributeChangedCallback = function (name, oldValue, newValue) {
    originalAttributeChangedCallback.call(this, name, oldValue, newValue);

    if (name === 'id' && this.id && !isInDocumentTree(this)) {
      this.delegate.off();
    }
  };
}

document.addEventListener('product:rerender', cleanupDuplicateProductDrawers);
document.addEventListener('DOMContentLoaded', cleanupDuplicateProductDrawers);
document.addEventListener('shopify:section:load', cleanupDuplicateProductDrawers);

customElements.whenDefined('x-drawer').then(() => {
  patchDialogElementForFragmentDrawers();
  cleanupDuplicateProductDrawers();
});

class FaqFilter extends HTMLElement {
  static VISIBLE_LIMIT = 5;

  connectedCallback() {
    this.nav = this.querySelector('.faq-filter__nav');
    this.indicator = this.querySelector('.faq-filter__indicator');
    this.buttons = Array.from(this.querySelectorAll('[data-faq-filter]'));
    this.items = Array.from(this.querySelectorAll('[data-faq-category]'));
    this.moreWrap = this.querySelector('.faq-filter__more');
    this.moreToggle = this.querySelector('[data-faq-filter-more]');
    this._abortController = new AbortController();
    this._activeFilter = 'all';
    this._expanded = false;

    if (!this.nav || !this.indicator || !this.buttons.length || !this.items.length) {
      return;
    }

    const { signal } = this._abortController;

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        this.setFilter(button.dataset.faqFilter);
      }, { signal });
    });

    this.moreToggle?.addEventListener('click', () => {
      if (this._expanded) {
        this.collapseVisible();
      } else {
        this.expandVisible();
      }
    }, { signal });

    this._scrollParent = this.nav.closest('.scroll-area') || this.nav;
    this._scrollParent.addEventListener('scroll', () => this.updateIndicatorPosition(), { passive: true, signal });
    window.addEventListener('resize', () => this.updateIndicatorPosition(), { signal });

    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this.updateIndicatorPosition());
      this._resizeObserver.observe(this.nav);
      this.buttons.forEach((button) => this._resizeObserver.observe(button));
    }

    this.setFilter('all', { animate: false });
    requestAnimationFrame(() => {
      this.nav.classList.add('is-ready');
      this.updateIndicatorPosition();
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._resizeObserver?.disconnect();
  }

  setFilter(filter, { animate = true } = {}) {
    this._activeFilter = filter;
    this._expanded = false;

    if (!animate) {
      this.indicator.style.transition = 'none';
    }

    this.buttons.forEach((button) => {
      const isSelected = button.dataset.faqFilter === filter;
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      const show = filter === 'all' || item.dataset.faqCategory === filter;
      item.toggleAttribute('hidden', !show);
      item.classList.remove('faq-list__item--collapsed');
    });

    this.applyVisibleLimit();
    this.updateIndicatorPosition();
    this.scrollActiveTabIntoView();

    if (!animate) {
      requestAnimationFrame(() => {
        this.indicator.style.transition = '';
      });
    }
  }

  getVisibleItems() {
    return this.items.filter((item) => !item.hidden);
  }

  applyVisibleLimit() {
    const visibleItems = this.getVisibleItems();
    const hasOverflow = visibleItems.length > FaqFilter.VISIBLE_LIMIT;

    if (!this._expanded) {
      visibleItems.forEach((item, index) => {
        if (index >= FaqFilter.VISIBLE_LIMIT) {
          item.classList.add('faq-list__item--collapsed');
        }
      });
    }

    if (this.moreWrap) {
      this.moreWrap.hidden = !hasOverflow;
    }

    this.syncMoreToggle(hasOverflow);
    this.classList.toggle('is-expanded', this._expanded);
  }

  syncMoreToggle(hasOverflow) {
    if (!this.moreToggle) {
      return;
    }

    const textEl = this.moreToggle.querySelector('.faq-list__toggle-text');
    const moreText = this.moreToggle.dataset.readMore || 'See more';
    const lessText = this.moreToggle.dataset.readLess || 'See less';

    this.moreToggle.setAttribute('aria-expanded', this._expanded ? 'true' : 'false');

    if (textEl && hasOverflow) {
      textEl.textContent = this._expanded ? lessText : moreText;
    }
  }

  expandVisible() {
    this._expanded = true;

    this.items.forEach((item) => {
      item.classList.remove('faq-list__item--collapsed');
    });

    this.applyVisibleLimit();
  }

  collapseVisible() {
    this._expanded = false;
    this.applyVisibleLimit();

    this.moreToggle?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  updateIndicatorPosition() {
    const activeButton = this.buttons.find(
      (button) => button.dataset.faqFilter === this._activeFilter
    );

    if (!activeButton || !this.indicator) {
      return;
    }

    this.indicator.style.width = `${activeButton.offsetWidth}px`;
    this.indicator.style.transform = `translateX(${activeButton.offsetLeft}px)`;
  }

  scrollActiveTabIntoView() {
    const activeButton = this.buttons.find(
      (button) => button.dataset.faqFilter === this._activeFilter
    );
    const scrollParent = this._scrollParent;

    if (!activeButton || !scrollParent || scrollParent === this.nav) {
      return;
    }

    const maxScrollLeft = scrollParent.scrollWidth - scrollParent.clientWidth;

    if (maxScrollLeft <= 0) {
      return;
    }

    const targetLeft = activeButton.offsetLeft - (scrollParent.clientWidth - activeButton.offsetWidth) / 2;
    const clampedLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

    scrollParent.scrollTo({
      left: clampedLeft,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('faq-filter')) {
  customElements.define('faq-filter', FaqFilter);
}

class FaqList extends HTMLElement {
  static VISIBLE_LIMIT = 5;

  connectedCallback() {
    this.toggle = this.querySelector('[data-faq-list-toggle]');
    this.textEl = this.toggle?.querySelector('.faq-list__toggle-text');
    this._expanded = false;

    if (!this.toggle) {
      return;
    }

    this.moreText = this.toggle.dataset.readMore || 'See more';
    this.lessText = this.toggle.dataset.readLess || 'See less';
    this.toggle.addEventListener('click', () => {
      if (this._expanded) {
        this.collapse();
      } else {
        this.expand();
      }
    });
  }

  getItems() {
    return Array.from(this.children).filter((child) => child.matches('.faq-list__item, .faq-filter__item'));
  }

  syncToggle() {
    const hasOverflow = this.getItems().length > FaqList.VISIBLE_LIMIT;

    this.toggle.hidden = !hasOverflow;
    this.toggle.setAttribute('aria-expanded', this._expanded ? 'true' : 'false');

    if (this.textEl && hasOverflow) {
      this.textEl.textContent = this._expanded ? this.lessText : this.moreText;
    }

    this.classList.toggle('is-expanded', this._expanded);
  }

  expand() {
    this._expanded = true;

    this.getItems().forEach((item) => {
      item.classList.remove('faq-list__item--collapsed');
    });

    this.syncToggle();
  }

  collapse() {
    this._expanded = false;

    this.getItems().forEach((item, index) => {
      item.classList.toggle('faq-list__item--collapsed', index >= FaqList.VISIBLE_LIMIT);
    });

    this.syncToggle();
    this.toggle.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (!customElements.get('faq-list')) {
  customElements.define('faq-list', FaqList);
}

const FOOTER_MENU_ACCORDION_SELECTOR = '.shopify-section--footer .footer__menu-accordion';
const FOOTER_MENU_MOBILE_MEDIA_QUERY = '(max-width: 699px)';

function resetFooterMenuAccordionInlineHeight(accordion) {
  accordion.style.height = '';
  accordion.style.overflow = '';
}

function clearFooterMenuRowMinHeights(blockList) {
  blockList?.querySelectorAll(':scope > .footer__block--menu').forEach((menu) => {
    menu.style.minHeight = '';
  });
}

function finalizeFooterMenuAccordionOpen(accordion) {
  if (!window.matchMedia(FOOTER_MENU_MOBILE_MEDIA_QUERY).matches || !accordion.hasAttribute('open')) {
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!accordion.hasAttribute('open')) {
        return;
      }

      accordion.style.height = `${accordion.scrollHeight}px`;
      accordion.style.overflow = 'visible';

      requestAnimationFrame(() => {
        resetFooterMenuAccordionInlineHeight(accordion);
      });
    });
  });
}

function bindFooterMenuAccordionHeightFix(accordion) {
  if (accordion.dataset.footerMenuHeightFixBound === 'true') {
    return;
  }

  accordion.dataset.footerMenuHeightFixBound = 'true';

  accordion.querySelector('summary')?.addEventListener('click', () => {
    if (!window.matchMedia(FOOTER_MENU_MOBILE_MEDIA_QUERY).matches) {
      resetFooterMenuAccordionInlineHeight(accordion);
      clearFooterMenuRowMinHeights(accordion.closest('.footer__block-list'));
      return;
    }

    if (accordion.hasAttribute('open')) {
      clearFooterMenuRowMinHeights(accordion.closest('.footer__block-list'));
      return;
    }

    window.setTimeout(() => finalizeFooterMenuAccordionOpen(accordion), 280);
  });
}

function initFooterMenuAccordionHeightFix() {
  document.querySelectorAll(FOOTER_MENU_ACCORDION_SELECTOR).forEach(bindFooterMenuAccordionHeightFix);
}

document.addEventListener('DOMContentLoaded', initFooterMenuAccordionHeightFix);
document.addEventListener('shopify:section:load', initFooterMenuAccordionHeightFix);

/**
 * Cross-sell quick buy: keep the hidden _cross_sell line item property on the
 * modal product form (including after variant re-renders) so the 10% discount
 * still applies when the customer picks another color. Also skip the modal's
 * "Added to cart" confirmation and open the cart drawer instead.
 */
function injectCrossSellProperty(drawer) {
  const discount = drawer.getAttribute('data-cross-sell');
  if (discount == null || discount === '') {
    return;
  }

  const form = drawer.querySelector('form[is="product-form"], form[action*="/cart/add"]');
  if (!form) {
    return;
  }

  let input = form.querySelector('input[name="properties[_cross_sell]"]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_cross_sell]';
    form.appendChild(input);
  }

  input.value = discount;
}

function onCrossSellVariantAdd(event) {
  // Bound listener from constructor always wins on the prototype — intercept in
  // capture phase instead, stop the confirmation UI, then open the cart drawer.
  event.stopImmediatePropagation();
  this.hide();

  if (window.themeVariables?.settings?.cartType === 'drawer') {
    document.querySelector('cart-drawer')?.show();
  }
}

function bindCrossSellQuickBuyDrawer(QuickBuyDrawer) {
  if (QuickBuyDrawer.prototype.__crossSellPatched) {
    return;
  }

  QuickBuyDrawer.prototype.__crossSellPatched = true;
  const originalShow = QuickBuyDrawer.prototype.show;

  QuickBuyDrawer.prototype.show = async function (...args) {
    const result = await originalShow.apply(this, args);

    if (!this.hasAttribute('data-cross-sell')) {
      return result;
    }

    injectCrossSellProperty(this);

    if (!this.__crossSellObserver) {
      this.__crossSellObserver = new MutationObserver(() => injectCrossSellProperty(this));
      this.__crossSellObserver.observe(this, { childList: true, subtree: true });
    }

    if (!this.__crossSellVariantAddBound) {
      this.__crossSellVariantAddBound = true;
      this.addEventListener('variant:add', onCrossSellVariantAdd.bind(this), true);
    }

    return result;
  };
}

function initCrossSellQuickBuyPatch() {
  const QuickBuyDrawer = customElements.get('quick-buy-drawer');

  if (QuickBuyDrawer) {
    bindCrossSellQuickBuyDrawer(QuickBuyDrawer);
    return;
  }

  customElements.whenDefined('quick-buy-drawer').then((Defined) => {
    bindCrossSellQuickBuyDrawer(Defined);
  });
}

initCrossSellQuickBuyPatch();

/**
 * Product score slider info tooltips — toggle on tap; fixed-position so scroll parents don't clip.
 */
function closeProductScoreSliderInfo(except) {
  document.querySelectorAll('.product-score-sliders__info[aria-expanded="true"]').forEach((el) => {
    if (el !== except) {
      el.setAttribute('aria-expanded', 'false');
      resetProductScoreTooltipPosition(el);
    }
  });
}

function resetProductScoreTooltipPosition(trigger) {
  const tip = trigger?.querySelector('.product-score-sliders__tooltip');
  if (!tip) return;
  tip.classList.remove('is-fixed', 'is-above');
  tip.style.left = '';
  tip.style.top = '';
  tip.style.right = '';
  tip.style.bottom = '';
  tip.style.maxWidth = '';
  tip.style.maxHeight = '';
}

function positionProductScoreTooltip(trigger) {
  const tip = trigger?.querySelector('.product-score-sliders__tooltip');
  if (!tip) return;

  tip.classList.add('is-fixed');
  tip.classList.remove('is-above');
  tip.style.left = '0px';
  tip.style.top = '0px';
  tip.style.right = 'auto';
  tip.style.bottom = 'auto';

  const anchor = trigger.getBoundingClientRect();
  const gap = 10;
  const pad = 12;
  // Keep clear of floating chat / help widgets in the bottom-right.
  const bottomSafe = 110;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxWidth = Math.min(280, vw - pad * 2);
  tip.style.maxWidth = `${maxWidth}px`;
  tip.style.maxHeight = `${Math.min(Math.round(vh * 0.45), 280)}px`;

  const tipRect = tip.getBoundingClientRect();
  const spaceBelow = vh - bottomSafe - anchor.bottom - gap;
  const spaceAbove = anchor.top - pad - gap;

  let placeAbove = true;
  if (tipRect.height > spaceAbove && spaceBelow >= tipRect.height) {
    placeAbove = false;
  } else if (tipRect.height <= spaceAbove && tipRect.height <= spaceBelow) {
    // Prefer above when the trigger sits in the lower half of the viewport.
    placeAbove = anchor.bottom > vh * 0.45;
  } else if (spaceAbove >= spaceBelow) {
    placeAbove = true;
  } else {
    placeAbove = false;
  }

  tip.classList.toggle('is-above', placeAbove);

  let top = placeAbove ? anchor.top - tipRect.height - gap : anchor.bottom + gap;
  top = Math.min(Math.max(pad, top), vh - tipRect.height - pad);

  // Prefer aligning to the trigger; shift left if it would overflow the viewport.
  let left = anchor.left;
  if (left + tipRect.width > vw - pad) {
    left = vw - tipRect.width - pad;
  }
  if (left < pad) {
    left = pad;
  }

  // When opening below, nudge away from the bottom-right help FAB if needed.
  if (!placeAbove) {
    const fabLeft = vw - 220;
    const tipRight = left + tipRect.width;
    const tipBottom = top + tipRect.height;
    if (tipRight > fabLeft && tipBottom > vh - bottomSafe) {
      left = Math.max(pad, fabLeft - tipRect.width - gap);
    }
  }

  tip.style.left = `${Math.round(left)}px`;
  tip.style.top = `${Math.round(top)}px`;
}

function initProductScoreSliderInfo() {
  if (document.documentElement.dataset.productScoreSliderInfoInit === 'true') {
    return;
  }

  document.documentElement.dataset.productScoreSliderInfoInit = 'true';

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.product-score-sliders__info');

    if (trigger) {
      event.preventDefault();
      const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
      closeProductScoreSliderInfo(trigger);
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        positionProductScoreTooltip(trigger);
      } else {
        resetProductScoreTooltipPosition(trigger);
      }
      return;
    }

    closeProductScoreSliderInfo();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeProductScoreSliderInfo();
    }
  });

  document.addEventListener('pointerover', (event) => {
    const trigger = event.target.closest('.product-score-sliders__info');
    if (!trigger || window.matchMedia('(hover: none)').matches) return;
    positionProductScoreTooltip(trigger);
  });

  document.addEventListener('pointerout', (event) => {
    const trigger = event.target.closest('.product-score-sliders__info');
    if (!trigger || window.matchMedia('(hover: none)').matches) return;
    if (trigger.getAttribute('aria-expanded') === 'true') return;
    if (trigger.contains(event.relatedTarget)) return;
    resetProductScoreTooltipPosition(trigger);
  });

  window.addEventListener(
    'scroll',
    () => {
      document.querySelectorAll('.product-score-sliders__info[aria-expanded="true"]').forEach((el) => {
        positionProductScoreTooltip(el);
      });
    },
    true
  );
}

initProductScoreSliderInfo();

/**
 * Product score comparison — same product type peers, radio dropdown → modal.
 */
const PRODUCT_SCORE_COMPARE = {
  maxScore: 50,
  visualDots: 5,
  maxPeers: 3,
  spectrumColors: ['#8a7052', '#3f5f73', '#5f7a45', '#8a4f63'],
};

function getProductScoreCompareRoot(fromEl) {
  return fromEl?.closest?.('[data-product-score-compare]') || null;
}

function parseProductScoreCompareData(root) {
  const script = root?.querySelector('[data-score-compare-products]');
  if (!script) return [];
  try {
    return JSON.parse(script.textContent.trim());
  } catch (error) {
    console.warn('Product score compare JSON parse failed', error);
    return [];
  }
}

function resetProductScoreCompareDropdownPosition(dropdown) {
  if (!dropdown) return;

  dropdown.classList.remove('is-fixed');
  dropdown.style.top = '';
  dropdown.style.left = '';
  dropdown.style.right = '';
  dropdown.style.width = '';
  dropdown.style.maxHeight = '';

  const host = dropdown._compareHost;
  if (host && dropdown.parentElement !== host) {
    host.appendChild(dropdown);
  }
}

function positionProductScoreCompareDropdown(toggle, dropdown) {
  if (!toggle || !dropdown || dropdown.hidden) return;

  if (!dropdown._compareHost) {
    dropdown._compareHost = dropdown.parentElement;
  }
  if (dropdown.parentElement !== document.body) {
    document.body.appendChild(dropdown);
  }

  const rect = toggle.getBoundingClientRect();
  const gap = 6;
  const viewportPadding = 8;
  const width = Math.min(Math.max(rect.width, 256), window.innerWidth - viewportPadding * 2);
  let left = rect.right - width;

  if (left < viewportPadding) left = viewportPadding;
  if (left + width > window.innerWidth - viewportPadding) {
    left = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
  }

  const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
  const spaceAbove = rect.top - gap - viewportPadding;
  const preferredMax = Math.min(288, window.innerHeight * 0.55);
  const openBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
  const maxHeight = Math.min(preferredMax, Math.max(168, openBelow ? spaceBelow : spaceAbove));
  const top = openBelow
    ? rect.bottom + gap
    : Math.max(viewportPadding, rect.top - gap - maxHeight);

  dropdown.classList.add('is-fixed');
  dropdown.style.top = `${Math.round(top)}px`;
  dropdown.style.left = `${Math.round(left)}px`;
  dropdown.style.right = 'auto';
  dropdown.style.width = `${Math.round(width)}px`;
  dropdown.style.maxHeight = `${Math.round(maxHeight)}px`;
}

function closeProductScoreCompareDropdowns(exceptRoot) {
  document.querySelectorAll('[data-product-score-compare]').forEach((root) => {
    if (root === exceptRoot) return;
    const toggle = root.querySelector('[data-score-compare-toggle]');
    const dropdown = root._compareDropdown || root.querySelector('[data-score-compare-dropdown]');
    toggle?.setAttribute('aria-expanded', 'false');
    if (dropdown) {
      dropdown.hidden = true;
      resetProductScoreCompareDropdownPosition(dropdown);
    }
  });

  document.querySelectorAll('[data-score-compare-dropdown].is-fixed').forEach((dropdown) => {
    const hostRoot = dropdown._compareHost?.closest?.('[data-product-score-compare]');
    if (hostRoot && hostRoot === exceptRoot) return;
    const toggle = hostRoot?.querySelector('[data-score-compare-toggle]');
    toggle?.setAttribute('aria-expanded', 'false');
    dropdown.hidden = true;
    resetProductScoreCompareDropdownPosition(dropdown);
  });
}

function openProductScoreCompareDropdown(root) {
  const toggle = root?.querySelector('[data-score-compare-toggle]');
  let dropdown = root?.querySelector('[data-score-compare-dropdown]');
  if (!dropdown) dropdown = root?._compareDropdown;
  if (!root || !toggle || !dropdown) return;

  closeProductScoreCompareDropdowns(root);
  root._compareDropdown = dropdown;
  toggle.setAttribute('aria-expanded', 'true');
  dropdown.hidden = false;
  positionProductScoreCompareDropdown(toggle, dropdown);
  syncProductScoreCompareSubmit(root, dropdown);
}

function renderProductScoreDots(value) {
  const max = PRODUCT_SCORE_COMPARE.maxScore;
  const dots = PRODUCT_SCORE_COMPARE.visualDots;
  const units = max / dots;
  let html = '<div class="product-score-sliders__dots product-score-compare-modal__dots" aria-hidden="true">';

  for (let i = 1; i <= dots; i += 1) {
    const start = (i - 1) * units;
    const amount = value - start;
    let fill = 0;
    if (amount >= units) fill = 100;
    else if (amount > 0) fill = Math.round((amount / units) * 100);

    const stateClass = fill === 100 ? ' is-filled' : fill > 0 ? ' is-partial' : '';
    html += `<span class="product-score-sliders__dot${stateClass}" style="--dot-fill: ${fill}%;"><span class="product-score-sliders__dot-fill"></span></span>`;
  }

  html += '</div>';
  return html;
}

function renderProductScoreSpectrumTrack(value) {
  const max = PRODUCT_SCORE_COMPARE.maxScore;
  const pct = Math.max(0, Math.min(100, Math.round(((Number(value) || 0) / max) * 100)));
  return `
    <div class="product-score-sliders__track product-score-compare-modal__track" style="--score-progress-pct: ${pct};" aria-hidden="true">
      <span class="product-score-sliders__rail"></span>
      <span class="product-score-sliders__marker"></span>
    </div>
  `;
}

function assignSpectrumPinOffsets(pins) {
  const byPct = new Map();

  pins.forEach((pin) => {
    const key = String(pin.pct);
    if (!byPct.has(key)) byPct.set(key, []);
    byPct.get(key).push(pin.index);
  });

  const byIndex = {};
  byPct.forEach((indexes) => {
    const n = indexes.length;
    indexes
      .slice()
      .sort((a, b) => a - b)
      .forEach((index, stagger) => {
        const centered = stagger - (n - 1) / 2;
        byIndex[index] = {
          pinY: n > 1 ? Math.round(centered * 6) : 0,
        };
      });
  });

  return byIndex;
}

function renderProductScoreSharedSpectrumTrack(entries) {
  const max = PRODUCT_SCORE_COMPARE.maxScore;
  const pins = (entries || []).map((entry, index) => ({
    index,
    title: entry.title || '',
    pct: Math.max(0, Math.min(100, Math.round(((Number(entry.value) || 0) / max) * 100))),
    color: PRODUCT_SCORE_COMPARE.spectrumColors[index % PRODUCT_SCORE_COMPARE.spectrumColors.length],
  }));
  const byIndex = assignSpectrumPinOffsets(pins);

  const markers = pins
    .map((pin) => {
      const offset = byIndex[pin.index] || { pinY: 0 };
      const title = escapeProductScoreHtml(pin.title);
      return `
        <span
          class="product-score-compare-modal__pin"
          style="--marker-pct: ${pin.pct}; --marker-color: ${pin.color}; --pin-y: ${offset.pinY}px; z-index: ${pin.index + 1};"
          title="${title}"
        >
          <span class="product-score-compare-modal__marker"></span>
        </span>
      `;
    })
    .join('');

  return `
    <div class="product-score-sliders__track product-score-compare-modal__track product-score-compare-modal__track--shared" aria-hidden="true">
      <span class="product-score-sliders__rail"></span>
      ${markers}
    </div>
  `;
}

function renderProductScoreCompareCell(rowMeta, value) {
  return `
    <div class="product-score-compare-modal__score">
      ${renderProductScoreDots(value)}
    </div>
  `;
}

function escapeProductScoreHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function decodeProductScoreEntities(value) {
  let text = String(value ?? '');
  // Run twice so &amp;#39; becomes ' after &amp; → & and &#39; → '
  for (let i = 0; i < 2; i += 1) {
    text = text
      .replace(/&amp;/g, '&')
      .replace(/&#0*39;|&apos;|&#x0*27;/gi, "'")
      .replace(/&quot;|&#0*34;/gi, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }
  return text;
}

const PRODUCT_SCORE_INFO_ICON =
  '<svg role="presentation" fill="none" focusable="false" stroke-width="1.5" width="13" height="13" class="icon icon-info" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.25" stroke="currentColor"></circle><path d="M8 7.25V11" stroke="currentColor" stroke-linecap="round"></path><circle cx="8" cy="5.1" r="0.75" fill="currentColor" stroke="none"></circle></svg>';

function renderProductScoreInfoButton(row, moreInfoTemplate) {
  const label = (row.label || '').trim();
  const info = (row.info || '').trim();
  const extraLines = Array.isArray(row.extra_lines) ? row.extra_lines.map((line) => String(line || '').trim()).filter(Boolean) : [];
  if (!info && !extraLines.length) return '';

  const ariaLabel = escapeProductScoreHtml(
    String(moreInfoTemplate || 'More information about __TITLE__').replace(/__TITLE__/g, label || 'score')
  );

  let tooltip = '';
  if (label) {
    tooltip += `<span class="product-score-sliders__tooltip-title">${escapeProductScoreHtml(label)}</span>`;
  }
  if (info) {
    tooltip += `<span class="product-score-sliders__tooltip-body">${escapeProductScoreHtml(info)}</span>`;
  }
  if (extraLines.length) {
    tooltip += `<ul class="product-score-sliders__tooltip-list">${extraLines
      .map((line) => `<li class="product-score-sliders__tooltip-list-item">${escapeProductScoreHtml(line)}</li>`)
      .join('')}</ul>`;
  }

  return `
    <button
      type="button"
      class="product-score-sliders__info"
      aria-expanded="false"
      aria-label="${ariaLabel}"
    >
      ${PRODUCT_SCORE_INFO_ICON}
      <span class="product-score-sliders__tooltip">${tooltip}</span>
    </button>
  `;
}

function buildProductScoreCompareModalHtml(compareProducts, options = {}) {
  const moreInfoTemplate = options.moreInfoTemplate || 'More information about __TITLE__';
  const aiHeading = options.aiHeading || 'Still not sure what product is the right one for you?';
  const aiText = options.aiText || 'Ask our AI — it can go more in depth about where the differences lie.';
  const aiSubmit = options.aiSubmit || 'Ask AI';
  const aiPlaceholder =
    options.aiPlaceholder || 'Eg.: What is the difference between FENRIS and FORTE?';
  const intro = decodeProductScoreEntities(options.intro || '').trim();

  const rowsMeta = [];
  const seen = new Set();

  compareProducts.forEach((product) => {
    (product.scores || []).forEach((row) => {
      const label = (row.label || '').trim();
      if (!label || seen.has(label)) return;
      seen.add(label);
      rowsMeta.push({
        label,
        display: (row.display || 'score') === 'spectrum' ? 'spectrum' : 'score',
        spectrum_l: (row.spectrum_l || '').trim(),
        spectrum_r: (row.spectrum_r || '').trim(),
        info: (row.info || '').trim(),
        extra_lines: Array.isArray(row.extra_lines) ? row.extra_lines : [],
      });
    });
  });

  const valueFor = (product, label) => {
    const match = (product.scores || []).find((row) => (row.label || '').trim() === label);
    return match ? Number(match.value) || 0 : 0;
  };

  const isSpectrumMode = rowsMeta.some((row) => row.display === 'spectrum');
  const columnCount = Math.max(compareProducts.length, 1);

  const productCard = (product, index) => {
    const image = product.image
      ? `<img class="product-score-compare-modal__product-image" src="${escapeProductScoreHtml(product.image)}" alt="" loading="lazy" width="240" height="240">`
      : `<div class="product-score-compare-modal__product-image" aria-hidden="true"></div>`;
    const color = PRODUCT_SCORE_COMPARE.spectrumColors[index % PRODUCT_SCORE_COMPARE.spectrumColors.length];
    const swatch = isSpectrumMode
      ? `<span class="product-score-compare-modal__swatch" style="--compare-swatch-color: ${color};" aria-hidden="true"></span>`
      : '';
    const price = (product.price || '').trim();
    const compareAt = (product.compare_at_price || '').trim();
    const priceHtml = price
      ? `<p class="product-score-compare-modal__product-price">
          ${compareAt ? `<s class="product-score-compare-modal__product-compare-at">${escapeProductScoreHtml(compareAt)}</s>` : ''}
          <span>${escapeProductScoreHtml(price)}</span>
        </p>`
      : '';
    return `
      <div class="product-score-compare-modal__product${isSpectrumMode ? ' product-score-compare-modal__product--swatched' : ''}">
        <div class="product-score-compare-modal__product-media">
          ${image}
          ${swatch}
        </div>
        <a class="product-score-compare-modal__product-link" href="${escapeProductScoreHtml(product.url)}">
          <span>${escapeProductScoreHtml(product.title)}</span>
        </a>
        ${priceHtml}
      </div>
    `;
  };
  const introHtml = intro
    ? `<p class="product-score-sliders__heading product-score-compare-modal__intro">${escapeProductScoreHtml(intro)}</p>`
    : '';
  const rows = rowsMeta
    .map((row) => {
      const isSpectrum = row.display === 'spectrum';
      const infoButton = isSpectrum ? '' : renderProductScoreInfoButton(row, moreInfoTemplate);

      if (isSpectrum) {
        const spectrumEntries = compareProducts.map((product) => ({
          title: product.title,
          value: valueFor(product, row.label),
        }));
        return `
          <li class="product-score-compare-modal__row product-score-compare-modal__row--spectrum">
            <div class="product-score-sliders__title product-score-compare-modal__row-label">
              ${infoButton}
              <p class="product-score-sliders__headline">${escapeProductScoreHtml(row.label)}</p>
            </div>
            <div class="product-score-compare-modal__row-body">
              <div class="product-score-compare-modal__poles" aria-hidden="true">
                <span class="product-score-sliders__pole product-score-sliders__pole--left">${escapeProductScoreHtml(row.spectrum_l || '')}</span>
                <span class="product-score-sliders__pole product-score-sliders__pole--right">${escapeProductScoreHtml(row.spectrum_r || '')}</span>
              </div>
              <div class="product-score-compare-modal__shared-score">
                ${renderProductScoreSharedSpectrumTrack(spectrumEntries)}
              </div>
            </div>
          </li>
        `;
      }

      const cells = compareProducts.map((product) => renderProductScoreCompareCell(row, valueFor(product, row.label))).join('');
      return `
        <li class="product-score-compare-modal__row">
          <div class="product-score-sliders__title product-score-compare-modal__row-label">
            ${infoButton}
            <p class="product-score-sliders__headline">${escapeProductScoreHtml(row.label)}</p>
          </div>
          <div class="product-score-compare-modal__row-body">
            <div class="product-score-compare-modal__row-scores">
              ${cells}
            </div>
          </div>
        </li>
      `;
    })
    .join('');

  const tableClass = isSpectrumMode
    ? 'product-score-compare-modal__table product-score-compare-modal__table--spectrum product-score-compare-modal__table--spectrum-shared'
    : 'product-score-compare-modal__table';

  return `
    <div class="product-score-compare-modal__content">
      ${introHtml}
      <div class="product-score-compare-modal__table-scroll">
        <div class="${tableClass}" style="--compare-columns: ${columnCount};">
          <div class="product-score-compare-modal__products">
            <div class="product-score-compare-modal__spacer" aria-hidden="true"></div>
            ${compareProducts.map((product, index) => productCard(product, index)).join('')}
          </div>
          <ul class="product-score-compare-modal__rows" role="list">
            ${rows}
          </ul>
        </div>
      </div>
      <div class="product-score-compare-modal__ai">
        <p class="product-score-compare-modal__ai-heading">${escapeProductScoreHtml(aiHeading)}</p>
        <p class="product-score-compare-modal__ai-text">${escapeProductScoreHtml(aiText)}</p>
        <form class="product-score-compare-modal__ai-form" data-score-compare-ai-form>
          <input
            type="text"
            class="product-score-compare-modal__ai-input"
            name="score-compare-ai-question"
            data-score-compare-ai-input
            placeholder="${escapeProductScoreHtml(aiPlaceholder)}"
            aria-label="${escapeProductScoreHtml(aiHeading)}"
            autocomplete="off"
          >
          <button type="submit" class="product-score-compare-modal__ai-submit" data-score-compare-ai-submit>
            ${escapeProductScoreHtml(aiSubmit)}
          </button>
        </form>
      </div>
    </div>
  `;
}

function syncProductScoreCompareSubmit(root, dropdown) {
  const scope = dropdown || root;
  if (!scope) return;

  const maxPeers = PRODUCT_SCORE_COMPARE.maxPeers;
  const options = Array.from(scope.querySelectorAll('[data-score-compare-option]'));
  const checkedCount = options.filter((input) => input.checked).length;
  const atLimit = checkedCount >= maxPeers;

  options.forEach((input) => {
    const optionLabel = input.closest('.product-score-sliders__compare-option');
    if (input.checked) {
      input.disabled = false;
      optionLabel?.classList.remove('is-disabled');
      return;
    }

    input.disabled = atLimit;
    optionLabel?.classList.toggle('is-disabled', atLimit);
  });

  const submit = scope.querySelector('[data-score-compare-submit]');
  if (submit) submit.disabled = checkedCount === 0;
}

function openProductScoreCompareModal(root, selectedIds) {
  const products = parseProductScoreCompareData(root);
  if (!products.length) return;

  const currentProduct = products[0];
  const selectedProducts = (selectedIds || [])
    .map((id) => products.find((item) => String(item.id) === String(id)))
    .filter(Boolean)
    .slice(0, PRODUCT_SCORE_COMPARE.maxPeers);

  if (!selectedProducts.length) return;

  const compareProducts = [currentProduct, ...selectedProducts];

  let modal = null;
  const modalId = root.getAttribute('data-score-compare-modal-id');
  if (modalId) {
    modal = document.getElementById(modalId);
  }
  if (!modal) {
    modal = root.querySelector('[data-score-compare-modal]');
  }
  // Move to body so product info overflow doesn't clip the dialog
  if (modal && modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  if (!modal) return;

  const body = modal.querySelector('[data-score-compare-modal-body]');
  if (!body) return;

  body.innerHTML = buildProductScoreCompareModalHtml(compareProducts, {
    moreInfoTemplate: decodeProductScoreEntities(root.getAttribute('data-score-more-info-template') || 'More information about __TITLE__'),
    aiHeading: decodeProductScoreEntities(root.getAttribute('data-score-ai-heading') || ''),
    aiText: decodeProductScoreEntities(root.getAttribute('data-score-ai-text') || ''),
    aiPlaceholder: decodeProductScoreEntities(root.getAttribute('data-score-ai-placeholder') || ''),
    aiSubmit: decodeProductScoreEntities(root.getAttribute('data-score-ai-submit') || ''),
    intro: decodeProductScoreEntities(root.getAttribute('data-score-intro') || ''),
  });
  modal.hidden = false;
  document.documentElement.classList.add('lock');
  modal.querySelector('.product-score-compare-modal__dialog')?.focus();
}

function closeProductScoreCompareModal() {
  closeProductScoreSliderInfo();
  document.querySelectorAll('[data-score-compare-modal]').forEach((modal) => {
    modal.hidden = true;
    const body = modal.querySelector('[data-score-compare-modal-body]');
    if (body) body.innerHTML = '';
  });
  document.documentElement.classList.remove('lock');
}

function dispatchMimirStartConversation(question) {
  const detail = String(question || '').trim();
  if (!detail) return;

  const now = Date.now();
  const state = (window.__argonMimirStart = window.__argonMimirStart || {
    lastAt: 0,
    lastDetail: '',
  });

  // Prevent duplicate prompts if the submit handler is bound more than once
  if (state.lastDetail === detail && now - state.lastAt < 2000) {
    return;
  }

  state.lastDetail = detail;
  state.lastAt = now;

  window.dispatchEvent(
    new CustomEvent('mimir:start-conversation', {
      detail,
    })
  );
}

function initProductScoreCompare() {
  if (window.__argonProductScoreCompareInit) {
    return;
  }
  window.__argonProductScoreCompareInit = true;

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-score-compare-toggle]');
    if (toggle) {
      event.preventDefault();
      const root = getProductScoreCompareRoot(toggle);
      if (!root) return;

      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      if (willOpen) {
        openProductScoreCompareDropdown(root);
      } else {
        closeProductScoreCompareDropdowns();
      }
      return;
    }

    if (event.target.closest('[data-score-compare-close]')) {
      event.preventDefault();
      closeProductScoreCompareModal();
      return;
    }

    if (
      !event.target.closest('[data-score-compare-dropdown]') &&
      !event.target.closest('[data-score-compare-toggle]')
    ) {
      closeProductScoreCompareDropdowns();
    }
  });

  document.addEventListener('click', (event) => {
    const submit = event.target.closest('[data-score-compare-submit]');
    if (!submit || submit.disabled) return;

    event.preventDefault();
    const dropdown = submit.closest('[data-score-compare-dropdown]');
    const root =
      getProductScoreCompareRoot(submit) ||
      dropdown?._compareHost?.closest?.('[data-product-score-compare]') ||
      null;
    if (!root) return;

    const selectedIds = Array.from(
      (dropdown || root).querySelectorAll('[data-score-compare-option]:checked')
    )
      .map((input) => input.value)
      .slice(0, PRODUCT_SCORE_COMPARE.maxPeers);
    if (!selectedIds.length) return;

    closeProductScoreCompareDropdowns();
    openProductScoreCompareModal(root, selectedIds);
  });

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-score-compare-option]');
    if (!option) return;

    const dropdown = option.closest('[data-score-compare-dropdown]');
    const root =
      getProductScoreCompareRoot(option) ||
      dropdown?._compareHost?.closest?.('[data-product-score-compare]') ||
      null;
    if (!root) return;

    const scope = dropdown || root;
    const checked = scope.querySelectorAll('[data-score-compare-option]:checked');
    if (option.checked && checked.length > PRODUCT_SCORE_COMPARE.maxPeers) {
      option.checked = false;
    }

    syncProductScoreCompareSubmit(root, dropdown);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-score-compare-ai-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const input = form.querySelector('[data-score-compare-ai-input]');
    let question = (input?.value || '').trim();

    if (!question) {
      question = String(input?.placeholder || '')
        .replace(/^(Eg\.|Fx|z\.\s*B\.|Ex\.|Es\.|Esim\.|Np\.)\s*:\s*/i, '')
        .trim();
    }

    if (!question) {
      input?.focus();
      return;
    }

    dispatchMimirStartConversation(question);
    closeProductScoreCompareModal();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeProductScoreCompareModal();
    closeProductScoreCompareDropdowns();
  });

  window.addEventListener(
    'scroll',
    () => {
      document.querySelectorAll('[data-score-compare-dropdown].is-fixed:not([hidden])').forEach((dropdown) => {
        const host = dropdown._compareHost;
        const root = host?.closest?.('[data-product-score-compare]');
        const toggle = root?.querySelector('[data-score-compare-toggle]');
        positionProductScoreCompareDropdown(toggle, dropdown);
      });
    },
    true
  );

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-score-compare-dropdown].is-fixed:not([hidden])').forEach((dropdown) => {
      const host = dropdown._compareHost;
      const root = host?.closest?.('[data-product-score-compare]');
      const toggle = root?.querySelector('[data-score-compare-toggle]');
      positionProductScoreCompareDropdown(toggle, dropdown);
    });
  });
}

initProductScoreCompare();
